import type {
  AppointmentRequestResponse,
  AppointmentRequestResponseStatus,
  AppointmentResponse,
  AppointmentResponseStatus,
  CalendarDoctorResponse,
  CalendarResponse,
  CalendarRoomResponse,
  CalendarShiftResponse,
  CalendarTimeOffResponse,
  ClinicProcedureResponse,
  FreeSlotResponse,
  PatientSummaryResponse,
  WaitListResponse,
  WaitListResponseStatus,
} from '@/api/generated/model';
import type { StatusTone } from '@bookora/ui';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE CALENDAR'S VOCABULARY — aliases over the generated model, not copies
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Every name here is an alias or a `Pick` of an Orval-generated type. Nothing is
 * re-declared. A field renamed in the backend DTO therefore breaks this feature
 * at compile time, which is the entire point — a hand-written mirror would
 * compile happily against a payload that no longer exists.
 *
 * The aliases exist for two reasons and no others: the generated names carry a
 * `…Response` suffix that reads badly in component props, and two of them
 * (`BookingRequest`, `WaitListEntry`) are what the product calls the thing.
 *
 * ── WHAT IS STILL `{ [key: string]: unknown }`, AND WHY THAT IS CORRECT ─────
 *
 * `CalendarShiftResponse.breaks` and `WaitListResponse.preferredWindows` are
 * genuinely free-form JSON columns in the schema — the OpenAPI document says so
 * and the generator is right to say `unknown`. Both are narrowed at the point of
 * use with a `typeof` check rather than a cast, so a malformed row renders as
 * "no breaks" instead of `NaN`.
 *
 * ── NULLABLE vs OPTIONAL ────────────────────────────────────────────────────
 *
 * The generated fields are `?: T | null` — absent OR null. Code in this feature
 * therefore compares with `??`, never with `=== null`, because `undefined` and
 * `null` mean the same thing here and only one of them is what arrives.
 */

export type Appointment = AppointmentResponse;
export type AppointmentStatus = AppointmentResponseStatus;
export type CalendarDoctor = CalendarDoctorResponse;
export type CalendarRoom = CalendarRoomResponse;
export type CalendarShift = CalendarShiftResponse;
export type CalendarTimeOff = CalendarTimeOffResponse;

/** Everything the day/week board needs, from one `GET /appointments/calendar`. */
export type CalendarPayload = CalendarResponse;

export type BookingRequest = AppointmentRequestResponse;
export type BookingRequestStatus = AppointmentRequestResponseStatus;

export type WaitListEntry = WaitListResponse;
export type WaitListStatus = WaitListResponseStatus;

export type FreeSlot = FreeSlotResponse;

/**
 * The slice of the clinic tariff the calendar needs.
 *
 * A `Pick` rather than the whole `ClinicProcedureResponse`: the calendar reads
 * six of its thirty fields, and the full type drags in a nested
 * `ProcedureResponse` that every test fixture would then have to invent.
 *
 * `bufferMinutes` is the field this projection exists for — see `api.ts`.
 */
export type ProcedureOption = Pick<
  ClinicProcedureResponse,
  | 'id'
  | 'procedureId'
  | 'displayName'
  | 'priceRials'
  | 'durationMinutes'
  | 'bufferMinutes'
  | 'totalBlockMinutes'
  | 'requiresRoom'
  | 'isEnabled'
>;

/**
 * A patient the booking form is opened with, or pinned to.
 *
 * Only `id` and `fullName` are required: a waitlist row and a deep link know
 * the patient but not their file code, and the picker labels the value from
 * `GET /patients/:id` anyway. `phone` is widened because `/patients` returns
 * a plain `string` while an appointment's nested copy is `string | null`.
 */
export type PatientOption = Pick<PatientSummaryResponse, 'id' | 'fullName'> & {
  code?: string | null;
  phone?: string | null;
  isBlacklisted?: boolean;
};

/* -------------------------------------------------------------------------- */
/* Naming an appointment                                                      */
/* -------------------------------------------------------------------------- */

export interface AppointmentLabel {
  text: string;
  /**
   * `true` when the row has neither a patient nor a label — which is what a
   * WITHHELD patient looks like, not an empty one.
   */
  withheld: boolean;
  /**
   * `true` when the row has neither, and the viewer is someone the server
   * never withholds a patient from. Then the row genuinely has no patient —
   * see case 4 below.
   */
  unassigned?: boolean;
}

export interface AppointmentLabelOptions {
  /**
   * The viewer holds `patient.view_all`, so the server sends every patient it
   * has. A missing patient is then MISSING, not hidden.
   */
  seesAllPatients?: boolean;
}

/**
 * What to write on the block.
 *
 * Three cases, and the third is the one that matters:
 *
 *  1. `patient` present → the decrypted name the server chose to send.
 *  2. no patient but a `title` → blocked time («ناهار», «تعمیر دستگاه») or a
 *     phone booking that has no file yet. The clinic wrote that string.
 *  3. neither → the row exists on this doctor's calendar but its patient did
 *     not come with it. `toAppointmentResponse` emits `patient: null` whenever
 *     the relation is not on the row, and doctor-level scoping is exactly when
 *     that happens: a staff member without `patient.view_all` is shown that the
 *     slot is TAKEN without being shown by whom.
 *
 * Case 3 must never render as an empty string or as «زمان مسدود». Both read as
 * "this appointment is free / is not a real booking", and a receptionist acting
 * on either is double-booking a colleague's patient. It is labelled as withheld
 * and the caller draws it as such.
 *
 * 4. neither, for a viewer with `patient.view_all` → nothing was withheld from
 *    this person, so the booking really has no patient (the booking form
 *    used to accept one). Telling the clinic OWNER «با دسترسی فعلی‌تان مشخصات
 *    بیمار رو نمی‌بینید» sent them to support about a permission they hold;
 *    the row says «بدون بیمار» and the drawer offers to fix it.
 */
export function appointmentLabel(
  appointment: Appointment,
  options: AppointmentLabelOptions = {},
): AppointmentLabel {
  if (appointment.patient) return { text: appointment.patient.fullName, withheld: false };
  const title = appointment.title?.trim();
  if (title) return { text: title, withheld: false };
  if (options.seesAllPatients) {
    return { text: 'نوبت بدون بیمار', withheld: false, unassigned: true };
  }
  return { text: 'بیمار نمایش‌داده‌نشده', withheld: true };
}

/** One sentence explaining a withheld row, for a tooltip. */
export const WITHHELD_HINT = 'این زمان رزرو شده، ولی حساب شما به مشخصات بیمار دسترسی نداره.';

/** …and one for a row that simply has no patient. */
export const UNASSIGNED_HINT =
  'برای این نوبت بیماری انتخاب نشده. با «ویرایش نوبت» بیمار رو مشخص کنید.';

/**
 * Waitlist priority, in words. The column is 0–100 and «اولویت ۳» told a
 * manager nothing — is three high or low? The desk only ever needs two
 * answers, so the form writes one of two numbers and the list reads any
 * positive number back as «فوری».
 */
export const WAITLIST_PRIORITY = { NORMAL: 0, URGENT: 10 } as const;

export function isUrgent(entry: Pick<WaitListEntry, 'priority'>): boolean {
  return entry.priority > 0;
}

/**
 * The status graph, mirrored from
 * `backend/src/scheduling/scheduling.constants.ts`.
 *
 * Used only to decide which buttons to DRAW — the server re-checks with
 * `canTransition` and answers `APPOINTMENT_INVALID_STATUS` regardless. Cancel is
 * absent from every row on purpose: it has its own route, because it records a
 * reason and notifies the waitlist.
 */
export const STATUS_TRANSITIONS: Readonly<Record<AppointmentStatus, readonly AppointmentStatus[]>> =
  Object.freeze({
    REQUESTED: ['CONFIRMED', 'ARRIVED', 'NO_SHOW'],
    CONFIRMED: ['ARRIVED', 'IN_PROGRESS', 'NO_SHOW'],
    ARRIVED: ['IN_PROGRESS', 'DONE', 'NO_SHOW'],
    IN_PROGRESS: ['DONE'],
    DONE: [],
    CANCELLED: [],
    NO_SHOW: [],
  });

/**
 * The doctor the patient asked for — a preference, not a booking; the desk
 * may confirm with someone else. A blank name reads as "no preference".
 */
export function requestedDoctorName(request: BookingRequest): string | null {
  const name = request.doctorName?.trim();
  return name ? name : null;
}

/**
 * What each appointment status MEANS, as a `StatusTag` tone — the one map.
 *
 * The dashboard agenda, this drawer and the patient timeline each used to keep
 * their own, and they disagreed: «تأییدشده» was blue on the dashboard, blue-ish
 * «progress» here and green on the patient file. A receptionist learns a colour
 * on one screen and it has to mean the same thing on the next.
 */
export const APPOINTMENT_STATUS_TONE: Readonly<Record<AppointmentStatus, StatusTone>> = {
  REQUESTED: 'inert',
  CONFIRMED: 'progress',
  ARRIVED: 'warning',
  IN_PROGRESS: 'progress',
  DONE: 'success',
  CANCELLED: 'inert',
  NO_SHOW: 'danger',
};
